/**
 * WorkspaceLink, smart cross-link for marketing pages that send users
 * into their authenticated workspace.
 *
 * Behaviour:
 *   - If the user is signed in    → navigates to `to` (a /workspace/* path)
 *   - If the user is signed out   → opens the global auth modal in sign-up mode
 *     with `redirectTo=to` so we land them on the workspace target after sign-in
 *
 * Usage from any marketing page:
 *   import WorkspaceLink from '../components/WorkspaceLink';
 *   <WorkspaceLink to="/workspace/tools/cost-tracker" className="...">
 *     Open Cost Tracker
 *   </WorkspaceLink>
 *
 * The component renders as an <a> (so it gets proper hover/keyboard
 * semantics) and intercepts the click. Right-click "open in new tab"
 * still works for signed-in users; signed-out users get the auth modal
 * regardless (we can't deep-link them without auth).
 */
import React from 'react';
import { useMarketingAuth } from '../hooks/useMarketingAuth';
import { useAuthModal } from '../context/AuthModalContext';

const WorkspaceLink = ({ to, children, className, mode = 'signup', testId, ...rest }) => {
  const { isLoggedIn } = useMarketingAuth();
  const { open } = useAuthModal();

  const handleClick = (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (isLoggedIn) {
      window.location.href = to;
    } else {
      open(mode, { redirectTo: to });
    }
  };

  return (
    <a
      href={to}
      onClick={handleClick}
      className={className}
      data-testid={testId || 'workspace-link'}
      {...rest}
    >
      {children}
    </a>
  );
};

export default WorkspaceLink;
